import React, {
  ReactNode,
  MouseEvent,
} from 'react';
import isNil from 'lodash/isNil';
import AddIcon from '@mui/icons-material/Add';
import ToolTip from 'core/components/Tooltip';
import {
  useConfirmDialog,
} from 'core/hooks';
import {
  claimEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  Claim,
} from 'pages/Dashboard/pages/Billing/types';
import Button from 'pages/Dashboard/components/Button';
import clsx from 'clsx';

type Props = {
  claim: Claim;
  patientId: number;
  className?: string;
  children?: string | ReactNode;
  tooltip?: string;
};

const defaultClassName = '!p-0.5 !rounded-full !border-blue-500 !border !border-dashed items-center justify-center flex';

export const secondaryClaimDisabledReasonByCode: Record<string, string> = {
  secondary_claim_exists: 'Secondary claim already exists',
  secondary_eob_exists: 'Secondary EOB already exists',
} as const;

export default function CreateSecondaryButton({
  claim,
  patientId,
  className = defaultClassName,
  children,
  tooltip,
}: Props) {
  const { confirm, closeConfirm } = useConfirmDialog();
  const secondaryClaimActionState = claim?.secondaryClaimActionState;
  const {
    isVisible,
    isEnabled,
    disabledReasonCode,
    warningMessage,
    requiresWarning,
  } = secondaryClaimActionState ?? {};

  const resolvedTooltip = !isEnabled && !isNil(disabledReasonCode)
    ? secondaryClaimDisabledReasonByCode[disabledReasonCode]
    : tooltip;

  const onSecondaryClaimCreateOpen = () => {
    claimEventChannel.emit('onSecondaryClaimCreateOpen', { claim, patientId });
  };

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (requiresWarning) {
      confirm({
        title: 'Create Secondary Claim',
        content: warningMessage ?? '',
        onConfirm: () => {
          onSecondaryClaimCreateOpen();
          closeConfirm();
        },
      });
      return;
    }

    onSecondaryClaimCreateOpen();
  };

  return (
    isVisible ? (
      <ToolTip
        title={resolvedTooltip}
        hide={isNil(resolvedTooltip)}
      >
        <span className={clsx('inline-flex', {
          '!cursor-not-allowed': !isEnabled,
        })}
        >
          <Button
            variant="outlined"
            color="primary"
            type="button"
            className={clsx(className, 'create-secondary-button', {
              '!border-gray-400 !cursor-not-allowed': !isEnabled,
            })}
            onClick={handleClick}
            disabled={!isEnabled}
          >
            <AddIcon className="!w-4 !h-4 !text-inherit" />
            {children}
          </Button>
        </span>
      </ToolTip>
    ) : null
  );
}
